import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    CardBody, Row, Col, Card, Container, Form, Alert, Label, Input
} from "reactstrap";
import { Link } from "react-router-dom";
import { usePDFExport } from './ExportPDF';
import { usePocketBase } from '../../../PocketBaseContext';
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import logo from "../../../assets/images/logo.png";
import GetProducts from "./GetProducts";
import GetCustomerInfo from "./GetCustomerInfo";
import GetAndSelectNotes from "./GetAndSelectNotes";
import { generateInvoiceNumber } from './versionHandler';
import VersionHistoryTable from './VersionHistoryTable';
import {
    fetchExchangeRates,
    updateExchangeRates,
    calculateTotals,
    formatCurrency,
} from './currencyUtils';
import {
    createNewVersion,
    getVersionHistory,
    validateVersionCreation,
} from './versionHandler';
import VersionModal from './VersiyonModal';
import SaveHandler from './SaveHandler';

const InvoiceDetail = () => {
    document.title = "Teklif Olustur | Reform Panel";
    const { id } = useParams();
    const navigate = useNavigate();
    const pb = usePocketBase();
    const { exportToPDF } = usePDFExport();
    const [displayCurrency, setDisplayCurrency] = useState('TRY'); //save islemi icin 

    // State management
    const [title, setTitle] = useState('');
    const [customerInfo, setCustomerInfo] = useState(null);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedNotes, setSelectedNotes] = useState([]);
    const [exchangeRates, setExchangeRates] = useState(null);
    const [totals, setTotals] = useState({});
    const [rateUpdateStatus, setRateUpdateStatus] = useState(null);
    const [isUpdatingRates, setIsUpdatingRates] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [currentInvoice, setCurrentInvoice] = useState(null);
    const [versionHistory, setVersionHistory] = useState([]);
    const [versionNote, setVersionNote] = useState('');
    const [showVersionModal, setShowVersionModal] = useState(false);
    const [selectedContacts, setSelectedContacts] = useState([]);
    const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
    const [dueDate, setDueDate] = useState('');
    const [validUntil, setValidUntil] = useState('');
    const [status, setStatus] = useState('Taslak');
    const [invoiceNumber, setInvoiceNumber] = useState('');
    //save islemi icin 
    const handleDisplayCurrencyChange = (currency) => {
        setDisplayCurrency(currency);
    };

    // Durum seçenekleri
    const statusOptions = [
        { value: 'Taslak', label: 'Taslak' },
        { value: 'Beklemede', label: 'Beklemede' },
        { value: 'Onaylandı', label: 'Onaylandı' },
        { value: 'Reddedildi', label: 'Reddedildi' },
        { value: 'İptal Edildi', label: 'İptal Edildi' }
    ];

    useEffect(() => {
        const setupInitialData = async () => {
            try {
                setIsUpdatingRates(true);
                await updateExchangeRates(pb);
                const rates = await fetchExchangeRates(pb);
                setExchangeRates(rates);
                setRateUpdateStatus({
                    type: 'success',
                    message: 'Kurlar başarıyla yüklendi'
                });

                if (id) {
                    const record = await pb.collection('invoices').getOne(id, {
                        expand: 'customer,selected_contacts'
                    });

                    setCurrentInvoice(record);
                    setTitle(record.title || '');
                    setStatus(record.status || 'Taslak');
                    setInvoiceNumber(record.invoice_number || '');
                    setInvoiceDate(record.invoice_date || '');
                    setDueDate(record.due_date ? new Date(record.due_date).toISOString().split('T')[0] : '');
                    setValidUntil(record.valid_until ? new Date(record.valid_until).toISOString().split('T')[0] : '');
                    setDisplayCurrency(record.currency || 'TRY');

                    if (record.expand?.customer) {
                        setCustomerInfo(record.expand.customer);
                        if (record.expand.selected_contacts) {
                            setSelectedContacts(Array.isArray(record.expand.selected_contacts)
                                ? record.expand.selected_contacts
                                : [record.expand.selected_contacts]);
                        }
                    }

                    if (record.products) {
                        setSelectedProducts(typeof record.products === 'string' ? JSON.parse(record.products) : record.products);
                    }

                    if (record.notes) {
                        setSelectedNotes(typeof record.notes === 'string' ? JSON.parse(record.notes) : record.notes);
                    }

                    if (record.total_amount) {
                        setTotals(typeof record.total_amount === 'string' ? JSON.parse(record.total_amount) : record.total_amount);
                    }

                    const history = await getVersionHistory(pb, record.original_invoice || record.id);
                    setVersionHistory(history);
                }
            } catch (error) {
                console.error('Initial data loading error:', error);
                setRateUpdateStatus({
                    type: 'error',
                    message: 'Veri yüklenirken hata oluştu: ' + error.message
                });
            } finally {
                setIsUpdatingRates(false);
            }
        };

        setupInitialData();
    }, [id, pb]);

    // Versiyon kontrolü için status değişiklik izleyicisi
    useEffect(() => {
        if (currentInvoice && status !== currentInvoice.status && currentInvoice.id) {
            // Sadece gerçekten bir fark varsa ve ilk yükleme değilse (status Taslak'tan farklıysa veya currentInvoice set edilmişse)
            // Aslında setupInitialData içinde setStatus yapıyoruz, bu da bu effect'i tetikliyor.
            // Bunu engellemek için mevcut status ile currentInvoice.status'ü karşılaştırıyoruz ama setupInitialData bittiğinde bunlar eşit olmalı.
            setVersionNote(`Durum değişikliği: ${currentInvoice.status} -> ${status}`);
            setShowVersionModal(true);
        }
    }, [status]);

    useEffect(() => {
        const generateNewInvoiceNumber = () => {
            if (!currentInvoice?.invoice_number) {
                const newNumber = generateInvoiceNumber();
                setInvoiceNumber(newNumber);
            } else {
                setInvoiceNumber(currentInvoice.invoice_number);
            }
        };
        generateNewInvoiceNumber();
    }, [currentInvoice]);

    const handleCreateVersion = async () => {
        try {
            const validation = validateVersionCreation(currentInvoice);
            if (!validation.valid) {
                setRateUpdateStatus({
                    type: 'error',
                    message: validation.message
                });
                return;
            }

            const newVersion = await createNewVersion(pb, currentInvoice, versionNote);
            setCurrentInvoice(newVersion);
            setShowVersionModal(false);
            setVersionNote('');

            const history = await getVersionHistory(pb, currentInvoice.original_invoice || currentInvoice.id);
            setVersionHistory(history);

            setRateUpdateStatus({
                type: 'success',
                message: 'Yeni versiyon başarıyla oluşturuldu'
            });
        } catch (error) {
            console.error('Version creation error:', error);
            setRateUpdateStatus({
                type: 'error',
                message: 'Versiyon oluşturulurken hata oluştu'
            });
        }
    };

    const handleManualRateUpdate = async () => {
        setIsUpdatingRates(true);
        try {
            await updateExchangeRates(pb);
            const rates = await fetchExchangeRates(pb);
            setExchangeRates(rates);
            setRateUpdateStatus({
                type: 'success',
                message: 'Kurlar başarıyla güncellendi'
            });
        } catch (error) {
            setRateUpdateStatus({
                type: 'error',
                message: 'Kur güncellenirken hata oluştu: ' + error.message
            });
        } finally {
            setIsUpdatingRates(false);
        }
    };

    // Totals hesaplamasını güncelleyelim
    // useEffect(() => {
    //     if (exchangeRates && selectedProducts?.length) {
    //         // Sadece products veya exchangeRates değiştiğinde hesapla
    //         const newTotals = calculateTotals(selectedProducts, exchangeRates);
    //         console.log('Effect totals calculated:', {
    //             totals: newTotals,
    //             displayCurrency,
    //             productsCount: selectedProducts.length
    //         });
    //         setTotals(newTotals);
    //     }
    // }, [selectedProducts, exchangeRates]); // displayCurrency'yi dependency'den çıkaralım


    const handleCustomerSelect = (customer) => {
        setCustomerInfo(customer);
        if (customer.selectedContacts) {
            setSelectedContacts(customer.selectedContacts);
        }
    };

    const handleProductsSelect = (products, currency, calculatedTotals) => {
        console.log('Products Select Handler:', {
            products,
            currency,
            calculatedTotals
        });

        setSelectedProducts(products);

        if (currency) {
            setDisplayCurrency(currency);
        }

        // Gelen hesaplanmış totalleri direkt kullan
        if (calculatedTotals) {
            setTotals(calculatedTotals);
            console.log('Updated totals:', calculatedTotals);
        } else if (exchangeRates) {
            // Fallback olarak tekrar hesapla
            const newTotals = calculateTotals(products, exchangeRates);
            setTotals(newTotals);
            console.log('Fallback totals:', newTotals);
        }
    };



    const handleNotesSelect = (notes) => {
        setSelectedNotes(notes);
    };

    const handleSaveSuccess = (record) => {
        console.log('Teklif başarıyla kaydedildi:', record);
    };

    const handlePDFDownload = async () => {
        try {
            const currentData = {
                title,
                invoiceNo: document.getElementById('invoicenoInput').value,
                date: document.getElementById('date-field').value,
                status: document.getElementById('status-field').value,
                customerInfo,
                selectedProducts,
                selectedNotes,
                exchangeRates,
                totals
            };

            await exportToPDF(currentData);
        } catch (error) {
            console.error('PDF indirme hatası:', error);
            setRateUpdateStatus({
                type: 'error',
                message: 'PDF indirme sırasında hata oluştu: ' + error.message
            });
        }
    };

    // Durum badge'leri için yardımcı fonksiyon
    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'Taslak':
                return 'secondary';
            case 'Beklemede':
                return 'warning';
            case 'Onaylandı':
                return 'success';
            case 'Reddedildi':
                return 'danger';
            case 'İptal Edildi':
                return 'dark';
            default:
                return 'info';
        }
    };

    // Versiyon geçmişi için polling durumunu takip etmek için state ekleyelim
    const [isPollingEnabled, setIsPollingEnabled] = useState(true);
    const [refreshInterval] = useState(5000); // 5 saniye

    // Versiyon geçmişini yenileme fonksiyonu
    const refreshVersionHistory = useCallback(async () => {
        if (!currentInvoice?.id) return;

        try {
            const history = await getVersionHistory(pb, currentInvoice.original_invoice || currentInvoice.id);
            if (JSON.stringify(history) !== JSON.stringify(versionHistory)) {
                setVersionHistory(history);
                console.log('Versiyon geçmişi güncellendi:', history);
            }
        } catch (error) {
            console.error('Versiyon geçmişi güncellenirken hata:', error);
        }
    }, [currentInvoice, pb, versionHistory]);

    // Polling mekanizması için useEffect
    useEffect(() => {
        let intervalId;

        if (isPollingEnabled && currentInvoice?.id) {
            intervalId = setInterval(refreshVersionHistory, refreshInterval);
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [isPollingEnabled, refreshInterval, refreshVersionHistory]);


    const InvoiceHeaderSection = () => (
        <CardBody className="p-4 border-bottom border-bottom-dashed">
            <Row className="g-3">
                <Col lg={3} sm={6}>
                    <Label className="form-label">Teklif No</Label>
                    <Input
                        type="text"
                        className="form-control bg-light"
                        value={invoiceNumber}
                        readOnly
                    />
                </Col>
                <Col lg={3} sm={6}>
                    <Label className="form-label">Teklif Tarihi</Label>
                    <Input
                        type="date"
                        className="form-control"
                        value={invoiceDate}
                        onChange={(e) => setInvoiceDate(e.target.value)}
                    />
                </Col>
                <Col lg={3} sm={6}>
                    <Label className="form-label">Geçerlilik Tarihi</Label>
                    <Input
                        type="date"
                        className="form-control"
                        value={validUntil}
                        onChange={(e) => setValidUntil(e.target.value)}
                    />
                </Col>
                <Col lg={3} sm={6}>
                    <Label className="form-label">Son Ödeme Tarihi</Label>
                    <Input
                        type="date"
                        className="form-control"
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                    />
                </Col>
            </Row>
            <Row className="g-3 mt-2">
                <Col lg={3} sm={6}>
                    <Label className="form-label">Teklif Durumu</Label>
                    <Input
                        type="select"
                        className="form-control"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                    >
                        {statusOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Input>
                </Col>
                <Col lg={9} sm={6}>
                    {status !== 'Taslak' && (
                        <div className="alert alert-info mt-4 mb-0">
                            <i className="ri-information-line me-2"></i>
                            Teklif durumu değiştiğinde otomatik olarak yeni bir versiyon oluşturulacaktır.
                        </div>
                    )}
                </Col>
            </Row>
        </CardBody>
    );

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title="Teklif Detay" pageTitle="Teklifler" />
                <VersionModal
                    isOpen={showVersionModal}
                    toggle={() => setShowVersionModal(!showVersionModal)}
                    onCreateVersion={handleCreateVersion}
                    versionNote={versionNote}
                    setVersionNote={setVersionNote}
                />
                {rateUpdateStatus && (
                    <Alert
                        color={rateUpdateStatus.type === 'success' ? 'success' : 'danger'}
                        toggle={() => setRateUpdateStatus(null)}
                        isOpen={!!rateUpdateStatus}
                    >
                        {rateUpdateStatus.message}
                    </Alert>
                )}

                <Row className="justify-content-center">
                    <Col xxl={9}>
                        <Card>
                            <Form className="needs-validation" id="invoice_form">
                                <CardBody className="p-4 border-bottom border-bottom-dashed">
                                    <Row className="flex">
                                        <Col lg={4}>
                                            <div className="text-left">
                                                <img
                                                    src={logo}
                                                    alt="Reform Yangın Logo"
                                                    className="mb-4"
                                                    style={{ maxWidth: '250px' }}
                                                />
                                                <div className="address-section">
                                                    <p className="text-gray-600 mb-2"><strong>ADRES</strong></p>
                                                    <p className="mb-1">Pınartepe Mahallesi Mayıs Sokak No:7/18</p>
                                                    <p className="mb-1">Büyükçekmece / İSTANBUL</p>
                                                    <p>Posta Kodu: 34500</p>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col lg={8}>
                                            <div className="text-end">
                                                <h5 style={{ marginTop: '55px' }}>
                                                    <strong>Reform Yangın ve Yapı Teknolojileri Ltd. Şti.</strong>
                                                </h5>
                                                <p><strong>Vergi Dairesi/No:</strong> Büyükçekmece - 734 212 2268</p>
                                                <p><strong>Email:</strong> info@reformyangin.com</p>
                                                <p><strong>Website:</strong> www.reformyangin.com</p>
                                                <p><strong>Tel:</strong> 0850 888 19 81</p>
                                            </div>
                                        </Col>
                                    </Row>
                                </CardBody>

                                <CardBody className="p-4">
                                    <Row className="justify-content-center">
                                        <Col lg={8}>
                                            <div className="mb-3 text-center">
                                                <div className="input-group">
                                                    <input
                                                        type="text"
                                                        className="form-control text-center"
                                                        placeholder="İŞİN ADI: YANGIN SİSTEMLERİ REVİZYON İŞLEM TEKLİFİ"
                                                        value={title}
                                                        onChange={(e) => setTitle(e.target.value)}
                                                        style={{
                                                            fontSize: '16px',
                                                            fontWeight: '500'
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </CardBody>

                                <CardBody className="p-4">
                                    <Row className="g-3">
                                        <InvoiceHeaderSection />
                                    </Row>
                                </CardBody>

                                <CardBody className="p-4 border-top border-top-dashed">
                                    <GetCustomerInfo
                                        pb={pb}
                                        onCustomerSelect={handleCustomerSelect}
                                        initialCustomerInfo={customerInfo}
                                        initialSelectedContacts={selectedContacts}
                                    />
                                </CardBody>

                                <CardBody className="p-4 border-top border-top-dashed">
                                    <GetProducts
                                        pb={pb}
                                        onProductsSelect={handleProductsSelect}
                                        exchangeRates={exchangeRates}
                                        onCurrencyChange={handleDisplayCurrencyChange}
                                        initialCurrency={displayCurrency}
                                        initialProducts={selectedProducts}
                                    />
                                </CardBody>

                                <CardBody className="p-4 border-top border-top-dashed">
                                    <GetAndSelectNotes
                                        onNotesSelect={handleNotesSelect}
                                        initialNotes={selectedNotes}
                                    />
                                </CardBody>

                                <Card className="mb-3">
                                    <CardBody>
                                        <Row className="align-items-center">
                                            <Col md={6}>
                                                <h5 className="mb-0">
                                                    Güncel Kur Bilgileri
                                                </h5>
                                                {exchangeRates && (<small className="text-muted d-block mt-1">
                                                    {exchangeRates && (
                                                        <small className="text-muted ms-2">
                                                            (Teklif Tarihi: {exchangeRates?.turkishDate || '-'} | Kur Tarihi: {exchangeRates?.date || '-'} | Bülten: {exchangeRates?.bulletinNo || '-'})
                                                        </small>
                                                    )}
                                                    <p>Son güncelleme: {new Date().toLocaleTimeString('tr-TR')}</p>
                                                </small>
                                                )}
                                            </Col>
                                            <Col md={6} className="text-end">
                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    onClick={handleManualRateUpdate}
                                                    disabled={isUpdatingRates}
                                                >
                                                    {isUpdatingRates ? (
                                                        <span>
                                                            <i className="mdi mdi-loading mdi-spin me-2"></i>
                                                            Güncelleniyor...
                                                        </span>
                                                    ) : (
                                                        <span>
                                                            <i className="ri-refresh-line me-1"></i>
                                                            Kurları Güncelle
                                                        </span>
                                                    )}
                                                </button>
                                            </Col>
                                        </Row>

                                        {exchangeRates && (
                                            <Row className="mt-3">
                                                <Col md={4}>
                                                    <div className="border rounded p-3">
                                                        <h6>USD/TRY</h6>
                                                        <p className="mb-1">Alış: {formatCurrency(exchangeRates.currencies.USD.forexBuying, 'TRY')}</p>
                                                        <p className="mb-0">Satış: {formatCurrency(exchangeRates.currencies.USD.forexSelling, 'TRY')}</p>
                                                    </div>
                                                </Col>
                                                <Col md={4}>
                                                    <div className="border rounded p-3">
                                                        <h6>EUR/TRY</h6>
                                                        <p className="mb-1">Alış: {formatCurrency(exchangeRates.currencies.EUR.forexBuying, 'TRY')}</p>
                                                        <p className="mb-0">Satış: {formatCurrency(exchangeRates.currencies.EUR.forexSelling, 'TRY')}</p>
                                                    </div>
                                                </Col>
                                                <Col md={4}>
                                                    <div className="border rounded p-3">
                                                        <h6>GBP/TRY</h6>
                                                        <p className="mb-1">Alış: {formatCurrency(exchangeRates.currencies.GBP.forexBuying, 'TRY')}</p>
                                                        <p className="mb-0">Satış: {formatCurrency(exchangeRates.currencies.GBP.forexSelling, 'TRY')}</p>
                                                    </div>
                                                </Col>
                                            </Row>
                                        )}
                                    </CardBody>
                                </Card>

                                <div className="card ribbon-box border shadow-none mb-lg-0">
                                    <div className="card-body text-muted">
                                        <div className="ribbon-two ribbon-two-primary"><span>NOT</span></div>
                                        <p className="mb-0 text-end">Lütfen, imzalı ve kaşeli onayınızla birlikte fatura bilgilerinizi de belirtiniz.</p>
                                    </div>
                                </div>
                                {/* Action buttons section */}
                                <CardBody className="p-4">
                                    <div className="hstack gap-2 justify-content-end">
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => setShowVersionModal(true)}
                                            disabled={!currentInvoice}
                                        >
                                            <i className="ri-git-branch-line align-bottom me-1"></i>
                                            Yeni Versiyon
                                        </button>

                                        <SaveHandler
                                            pb={pb}
                                            title={title}
                                            customerInfo={customerInfo}
                                            selectedProducts={selectedProducts}
                                            selectedNotes={selectedNotes}
                                            selectedContacts={selectedContacts}
                                            currentInvoice={currentInvoice}
                                            isSaving={isSaving}
                                            setIsSaving={setIsSaving}
                                            setRateUpdateStatus={setRateUpdateStatus}
                                            setCurrentInvoice={setCurrentInvoice}
                                            displayCurrency={displayCurrency}  // Yeni prop
                                            displayTotal={totals[displayCurrency]} // Yeni prop
                                            totals={totals}
                                            dueDate={dueDate}
                                            validUntil={validUntil}
                                            invoiceDate={invoiceDate}
                                            onSaveSuccess={(record) => {
                                                console.log('Save Success:', {
                                                    record,
                                                    displayCurrency,
                                                    displayTotal: totals[displayCurrency],
                                                    allTotals: totals
                                                });
                                                handleSaveSuccess(record);
                                            }}
                                        />

                                        <button
                                            type="button"
                                            className="btn btn-info"
                                            onClick={handlePDFDownload}
                                        >
                                            <i className="ri-download-2-line align-bottom me-1"></i>
                                            PDF İndir
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-success"
                                        >
                                            <i className="ri-send-plane-fill align-bottom me-1"></i>
                                            Email Gönder
                                        </button>
                                    </div>
                                </CardBody>

                            </Form>
                        </Card>

                        {versionHistory.length > 0 && (
                            <VersionHistoryTable
                                versionHistory={versionHistory}
                                onRefresh={refreshVersionHistory}
                            />
                        )}
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default InvoiceDetail;